import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Copy, RefreshCw, Upload, Send } from 'lucide-react';

export default function AdminInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: '', private_email: '', plus_one_allowed: false, overnight_allowed: false, max_guests: 1 });
  const [loginSending, setLoginSending] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [guestCreating, setGuestCreating] = useState(false);
  const [pageMessage, setPageMessage] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [csvMessage, setCsvMessage] = useState('');

  const load = () => {
    apiGet('/api/invitations').then(r => setInvitations(r.data.invitations || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setDialogMessage('');
    try {
      if (editing) {
        if (!editing.guest_id && form.rsvp_status !== 'pending') {
          if (editing.link_candidate) {
            setDialogMessage('Der findes allerede en gæst med denne e-mail. Tilknyt gæsten nedenfor, før RSVP gemmes.');
            return;
          }
          if (!window.confirm('Der oprettes en gæsteprofil og RSVP gemmes. Der sendes ingen mail. Fortsæt?')) return;
          await apiPost(`/api/invitations/${editing.id}/create-guest`, form);
        } else {
          await apiPut(`/api/invitations/${editing.id}`, form);
        }
      } else {
        await apiPost('/api/invitations', form);
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ full_name: '', private_email: '', plus_one_allowed: false, overnight_allowed: false, max_guests: 1 });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDialogMessage(typeof detail === 'string' ? detail : detail?.message || 'Kunne ikke gemme invitationen');
    }
  };

  const handleEdit = (inv) => {
    setEditing(inv);
    setLoginMessage('');
    setDialogMessage('');
    setForm({ full_name: inv.full_name, private_email: inv.private_email, plus_one_allowed: inv.plus_one_allowed, overnight_allowed: inv.overnight_allowed, max_guests: inv.max_guests,
      rsvp_status: inv.rsvp?.attending === true ? 'yes' : inv.rsvp?.attending === false ? 'no' : 'pending' });
    setShowDialog(true);
  };

  const createOrLinkGuest = async () => {
    if (!editing || editing.guest_id) return;
    const candidate = editing.link_candidate;
    if (candidate?.already_linked) {
      setDialogMessage('Gæsten er allerede knyttet til en anden invitation og kan ikke tilknyttes her.');
      return;
    }
    const action = candidate ? `tilknytte den eksisterende gæst ${candidate.full_name}` : 'oprette en ny gæsteprofil';
    if (!window.confirm(`Du er ved at ${action}. Der sendes ingen mail. Fortsæt?`)) return;
    setGuestCreating(true);
    setDialogMessage('');
    try {
      const res = await apiPost(`/api/invitations/${editing.id}/create-guest`, { ...form, existing_guest_id: candidate?.id || undefined });
      const refreshed = await apiGet('/api/invitations');
      const invitationsNext = refreshed.data.invitations || [];
      setInvitations(invitationsNext);
      const current = invitationsNext.find((item) => item.id === editing.id);
      if (current) {
        handleEdit(current);
        setDialogMessage(res.data.message);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDialogMessage(typeof detail === 'string' ? detail : detail?.message || 'Gæsten kunne ikke oprettes eller tilknyttes');
    } finally {
      setGuestCreating(false);
    }
  };

  const sendLogin = async () => {
    if (!editing) return;
    const recipient = editing.login_email || editing.private_email;
    if (!window.confirm(`Send loginoplysninger til ${recipient}?`)) return;
    setLoginSending(true);
    setLoginMessage('');
    try {
      const res = await apiPost(`/api/invitations/${editing.id}/send-login`, { confirmed_email: recipient });
      setLoginMessage(res.data.message);
    } catch (err) {
      setLoginMessage(err.response?.data?.detail || 'Loginmail kunne ikke sendes');
    } finally {
      setLoginSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Slet denne invitation?')) return;
    try {
      await apiDelete(`/api/invitations/${id}`);
      load();
    } catch (err) {
      setPageMessage(err.response?.data?.detail || 'Kunne ikke slette invitationen');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const parseBool = (value) => ['true', 'yes', '1', 'y'].includes(String(value || '').trim().toLowerCase());
  const previewCsv = () => {
    const lines = csvText.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) {
      setCsvMessage('CSV skal indeholde en header og mindst én række.');
      setCsvRows([]);
      return;
    }
    const headers = lines[0].split(',').map(h => h.trim());
    const existing = new Set(invitations.map(i => (i.private_email || '').toLowerCase()));
    const seen = new Set();
    const rows = lines.slice(1).map((line, idx) => {
      const values = line.split(',').map(v => v.trim());
      const raw = Object.fromEntries(headers.map((h, i) => [h, values[i] || '']));
      const row = {
        full_name: raw.full_name || raw.name || '',
        private_email: (raw.private_email || raw.email || '').toLowerCase(),
        plus_one_allowed: parseBool(raw.plus_one_allowed),
        overnight_allowed: parseBool(raw.overnight_allowed),
        max_guests: Number(raw.max_guests || 1),
        row: idx + 2,
      };
      row.warning = !row.full_name || !row.private_email
        ? 'Navn eller e-mail mangler'
        : existing.has(row.private_email) || seen.has(row.private_email)
          ? 'Dublet-e-mail'
          : '';
      seen.add(row.private_email);
      return row;
    });
    setCsvRows(rows);
    setCsvMessage(`${rows.length} rækker læst, ${rows.filter(r => r.warning).length} advarsler.`);
  };

  const importCsvRows = async () => {
    try {
      const rows = csvRows.filter(r => !r.warning);
      const res = await apiPost('/api/invitations/import', { rows });
      setCsvMessage(`${res.data.created?.length || 0} importeret, ${res.data.skipped?.length || 0} sprunget over.`);
      setCsvText('');
      setCsvRows([]);
      load();
    } catch (err) {
      setCsvMessage(err.response?.data?.detail || 'Import mislykkedes');
    }
  };

  const batchSend = async () => {
    try {
      const res = await apiPost('/api/invitations/batch-send', {});
      setPageMessage(`Batch færdig: ${res.data.sent} sendt/logget, ${res.data.failed} fejlede.`);
      load();
    } catch (err) {
      setPageMessage(err.response?.data?.detail || 'Batchafsendelse mislykkedes');
    }
  };

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    invited: 'bg-blue-500/10 text-blue-400',
    opened: 'bg-indigo-500/10 text-indigo-400',
    registered: 'bg-emerald-500/10 text-emerald-400',
    completed: 'bg-green-500/10 text-green-400',
    declined: 'bg-red-500/10 text-red-400',
  };

  return (
    <div className="animate-fade-in" data-testid="admin-invitations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Invitationer</h1>
          <p className="text-sm text-slate-400 mt-1">{invitations.length} invitationer i alt</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-invitations-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={batchSend} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
            <Send className="w-4 h-4 mr-1" /> Send batch
          </Button>
          <Button onClick={() => { setEditing(null); setForm({ full_name: '', private_email: '', plus_one_allowed: false, overnight_allowed: false, max_guests: 1 }); setShowDialog(true); }}
            className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="create-invitation-btn">
            <Plus className="w-4 h-4 mr-1" /> Ny invitation
          </Button>
        </div>
      </div>
      {pageMessage && <div className="mb-5 rounded-lg border border-blue-500/20 bg-blue-500/10 px-4 py-3 text-sm text-blue-100" role="status">{pageMessage}</div>}

      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-medium text-slate-200">CSV-import</h2>
            <p className="text-xs text-slate-500">Headers: full_name, private_email, plus_one_allowed, overnight_allowed, max_guests</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={previewCsv} className="bg-slate-900/50 border-slate-700 text-slate-300">
              Forhåndsvis
            </Button>
            <Button size="sm" onClick={importCsvRows} disabled={csvRows.filter(r => !r.warning).length === 0}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Importér
            </Button>
          </div>
        </div>
        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={3}
          placeholder="full_name,private_email,plus_one_allowed,overnight_allowed,max_guests"
          className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-100 font-mono"
        />
        {csvMessage && <p className="text-xs text-slate-400 mt-2">{csvMessage}</p>}
        {csvRows.length > 0 && (
          <div className="mt-3 max-h-32 overflow-y-auto text-xs">
            {csvRows.map(row => (
              <div key={row.row} className={`flex justify-between border-b border-slate-700/30 py-1 ${row.warning ? 'text-amber-300' : 'text-slate-400'}`}>
                <span>{row.full_name} - {row.private_email}</span>
                <span>{row.warning || 'OK'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <p className="text-slate-400">Indlæser...</p> : (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invitations-table">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Navn</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">E-mail</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Kode</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">RSVP</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Valg</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id} className="border-b border-slate-700/20 hover:bg-slate-800/30" data-testid={`invitation-row-${inv.id}`}>
                    <td className="px-4 py-3 text-slate-200">{inv.full_name}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{inv.private_email}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => copyCode(inv.invite_code)} className="flex items-center gap-1 font-mono text-xs text-blue-400 hover:text-blue-300" data-testid={`copy-code-${inv.id}`}>
                        {inv.invite_code} <Copy className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${statusColors[inv.status] || ''}`}>{inv.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-300">
                      {inv.rsvp?.attending === true ? 'Kommer' : inv.rsvp?.attending === false ? 'Kommer ikke' : 'Ikke svaret'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {inv.plus_one_allowed && <span className="mr-2">+1</span>}
                      {inv.overnight_allowed && <span>Overnatning</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(inv)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200" data-testid={`edit-invitation-${inv.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(inv.id)} className="p-1.5 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400" data-testid={`delete-invitation-${inv.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {invitations.length === 0 && <p className="text-center py-8 text-slate-500 text-sm">Ingen invitationer endnu. Opret den første for at komme i gang.</p>}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>{editing ? 'Rediger invitation' : 'Ny invitation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="invitation-form">
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Fulde navn *</label>
              <input type="text" value={form.full_name} onChange={(e) => setForm(f => ({...f, full_name: e.target.value}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none"
                data-testid="invitation-name-input" required />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Privat e-mail *</label>
              <input type="email" value={form.private_email} onChange={(e) => setForm(f => ({...f, private_email: e.target.value}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none"
                data-testid="invitation-email-input" required />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Maks. antal gæster</label>
              <input type="number" min={1} max={10} value={form.max_guests} onChange={(e) => setForm(f => ({...f, max_guests: parseInt(e.target.value) || 1}))}
                className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none"
                data-testid="invitation-max-guests-input" />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.plus_one_allowed} onChange={(e) => setForm(f => ({...f, plus_one_allowed: e.target.checked}))}
                  className="rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500" data-testid="invitation-plus-one" />
                Ledsager
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.overnight_allowed} onChange={(e) => setForm(f => ({...f, overnight_allowed: e.target.checked}))}
                  className="rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500" data-testid="invitation-overnight" />
                Overnatning
              </label>
            </div>
            {editing && (
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">RSVP</label>
                <select value={form.rsvp_status || 'pending'} onChange={(e) => setForm(f => ({ ...f, rsvp_status: e.target.value }))}
                  className="w-full bg-slate-800/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none" data-testid="invitation-rsvp-select">
                  <option value="pending">Ikke svaret</option>
                  <option value="yes">Kommer</option>
                  <option value="no">Kommer ikke</option>
                </select>
              </div>
            )}
            {editing && (
              <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
                {editing.guest_id ? (
                  <p className="text-sm text-emerald-300">Invitationen er knyttet til en gæst.</p>
                ) : (
                  <>
                    <p className="mb-2 text-sm text-amber-200">Der findes endnu ingen tilknyttet gæsteprofil.</p>
                    {editing.link_candidate && <p className="mb-2 text-xs text-slate-300">Eksisterende gæst fundet: {editing.link_candidate.full_name} ({editing.link_candidate.private_email})</p>}
                    <Button type="button" variant="outline" onClick={createOrLinkGuest} disabled={guestCreating || editing.link_candidate?.already_linked}
                      className="w-full bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" data-testid="create-guest-from-invitation-btn">
                      {guestCreating ? 'Arbejder...' : editing.link_candidate ? 'Tilknyt eksisterende gæst' : 'Opret gæst fra invitation'}
                    </Button>
                  </>
                )}
              </div>
            )}
            {editing && (
              <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
                <p className="mb-2 text-xs text-slate-400">Modtager: {editing.login_email || editing.private_email}</p>
                <Button type="button" variant="outline" onClick={sendLogin} disabled={loginSending}
                  className="w-full bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" data-testid="send-login-btn">
                  <Send className="w-4 h-4 mr-1" /> {loginSending ? 'Sender...' : 'Send loginoplysninger'}
                </Button>
                {loginMessage && <p className="mt-2 text-xs text-slate-300" role="status">{loginMessage}</p>}
              </div>
            )}
            {dialogMessage && <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-sm text-blue-100" role="status">{dialogMessage}</div>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1 bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">Annuller</Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white" data-testid="invitation-save-btn">
                {editing ? 'Opdater' : 'Opret'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
